/**
 * SEO utilities for generating meta tags and structured data
 */

export interface SEOData {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  keywords?: string;
}

/**
 * Generate JSON-LD structured data for Organization
 */
export function generateOrganizationSchema(siteUrl: string): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Peptide Finder',
    url: siteUrl,
    description: 'Your shortcut to trusted peptides at the lowest price. Search peptides, browse vendors, and find research supplies.',
    logo: `${siteUrl}/favicon-32x32.png`,
  };
}

/**
 * Generate JSON-LD structured data for Product
 */
export function generateProductSchema(product: {
  name: string;
  description?: string;
  price?: number;
  currency?: string;
  url?: string;
  vendor?: string;
}): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || product.name,
    ...(product.price && {
      offers: {
        '@type': 'Offer',
        price: product.price,
        priceCurrency: product.currency || 'USD',
        availability: 'https://schema.org/InStock',
        url: product.url,
        seller: {
          '@type': 'Organization',
          name: product.vendor || 'Vendor',
        },
      },
    }),
    ...(product.url && { url: product.url }),
  };
}

/**
 * Generate JSON-LD structured data for LocalBusiness (Vendor)
 */
export function generateLocalBusinessSchema(vendor: {
  name: string;
  url: string;
  description?: string;
}): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: vendor.name,
    url: vendor.url,
    description: vendor.description || `${vendor.name} - Peptide vendor`,
  };
}

/**
 * Generate JSON-LD structured data for BreadcrumbList
 */
export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Generate JSON-LD structured data for WebSite with SearchAction
 */
export function generateWebSiteSchema(siteUrl: string, searchUrl: string): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Peptide Finder',
    url: siteUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${searchUrl}?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Get base URL from current location
 */
export function getBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}`;
  }
  return 'https://peptidefinder.com';
}

/**
 * Generate JSON-LD structured data for WebPage
 */
export function generateWebPageSchema(page: {
  url: string;
  title: string;
  description: string;
}): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: page.url,
    name: page.title,
    description: page.description,
  };
}

/**
 * Generate canonical URL
 */
export function generateCanonicalUrl(path: string): string {
  const base = getBaseUrl();
  return `${base}${path}`;
}

